import React, { useState, useEffect } from "react";
import { IoIosArrowDown } from "react-icons/io";
import { BsLightningCharge } from "react-icons/bs";
import { Form } from "react-bootstrap";
import axios from "axios";
import { useLocation } from "react-router-dom";

function PowerUp({ onDataChange }) {
  const [showForm, setShowForm] = useState(false);
  const [radioValue, setRadioValue] = useState("A");
  const [checkbox1, setCheckbox1] = useState(false);
  const [checkbox2, setCheckbox2] = useState(false);
  const [activeButton, setActiveButton] = useState("fromAll");
  const [tags, setTags] = useState([]);
  const [textFieldValue, setTextFieldValue] = useState("0");
  const location = useLocation();
  const { assessmentId } = location.state || {};
  const maxUniformCount = tags.length
    ? Math.min(...tags.map((tag) => Number(tag.total) || 0))
    : 0;

  const buttonStyle = {
    width: "100%",
    backgroundColor: "white",
    color: "#A63D00",
    borderColor: "#A63D00",
    fontFamily: "Manrope-Regular",
  };

  // Fetch tags when the component mounts
  useEffect(() => {
    if (assessmentId) {
      axios
        .get(`${process.env.REACT_APP_API}/getTagsWithCounts`, {
          params: { assessment_id: assessmentId },
        })
        .then((response) => {
          if (response.data.success) {
            const loadedTags = response.data.data.map((tag) => ({
                tags_id: tag.tags_id,
                tags_name: tag.tag_name,
                total: tag.question_count,
                value: Math.min(
                  Number(tag.selected_count) || 0,
                  Number(tag.question_count) || 0,
                ),
              }));
            setTags(loadedTags);
            if (loadedTags.some((tag) => tag.value > 0)) {
              setActiveButton("byTags");
            }
          }
        })
        .catch((error) => {
          console.error("Error fetching tags with counts:", error);
        });
    }
  }, [assessmentId]);

  // Update parent data when any relevant state changes
  useEffect(() => {
    onDataChange({
      radioValue,
      checkbox1,
      checkbox2,
      textFieldValue,
      activeButton,
      tags,
    });
  }, [radioValue, checkbox1, checkbox2, textFieldValue, activeButton, tags, onDataChange]);

  const handleToggleForm = () => {
    setShowForm(!showForm);
  };

  const handleRadioChange = (e) => {
    setRadioValue(e.target.value);
  };

  const handleCheckbox1 = () => {
    setCheckbox1((prev) => !prev);
  };

  const handleCheckbox2 = () => {
    setCheckbox2((prev) => !prev);
  };

  const handleTextFieldChange = (e) => {
    const value = Math.min(
      Math.max(parseInt(e.target.value, 10) || 0, 0),
      maxUniformCount,
    );
    setTextFieldValue(String(value));
    setTags((prevTags) =>
      prevTags.map((tag) => ({ ...tag, value: parseInt(value, 10) || 0 }))
    );
  };

  const handleTagInputChange = (e, tagId) => {
    setTags((prevTags) =>
      prevTags.map((tag) =>
        tag.tags_id === tagId
          ? {
              ...tag,
              value: Math.min(
                Math.max(parseInt(e.target.value, 10) || 0, 0),
                Number(tag.total) || 0,
              ),
            }
          : tag
      )
    );
  };

  const handleClick = (buttonName) => {
    setActiveButton(buttonName);
  };

  return (
    <div style={{ fontFamily: "Manrope-Regular" }}>
      <button
        className="btn btn-outline-primary d-flex align-items-center justify-content-between mt-2"
        style={buttonStyle}
        onClick={handleToggleForm}
      >
        <BsLightningCharge size={30} />
        <span>Power Up</span>
        <IoIosArrowDown />
      </button>
      {showForm && (
        <div className="mt-2">
          <form className="text-start p-4 border rounded">
            <h6 className="fw-bold">Pick a random subset</h6>
            <div
              className="btn-group mb-3"
              role="group"
              aria-label="Toggle Buttons"
            >
              <button
                type="button"
                className={`btn ${
                  activeButton === "fromAll"
                    ? "btn-secondary"
                    : "btn-outline-secondary"
                }`}
                onClick={() => handleClick("fromAll")}
              >
                  Same count per tag
              </button>
              <button
                type="button"
                className={`btn ${
                  activeButton === "byTags"
                    ? "btn-secondary"
                    : "btn-outline-secondary"
                }`}
                onClick={() => handleClick("byTags")}
              >
                By Tags
              </button>
            </div>

            {activeButton === "byTags" && (
              <div className="mb-4">
                {tags.map((tag) => (
                  <div
                    key={tag.tags_id}
                    className="d-flex align-items-center justify-content-between mb-2"
                  >
                    <span
                      className="me-2"
                      style={{ width: "30%", fontSize: "13px" }}
                    >
                      {tag.tags_name.toLowerCase()}
                    </span>
                    <input
                      type="number"
                      min="0"
                      max={tag.total}
                      value={tag.value}
                      onChange={(e) => handleTagInputChange(e, tag.tags_id)}
                      className="form-control w-25 text-center"
                    />
                    <span
                      className="text-muted"
                      style={{ width: "20%", fontSize: "13px" }}
                    >
                      out of <b>{tag.total}</b>
                    </span>
                  </div>
                ))}
              </div>
            )}

            {activeButton === "fromAll" && (
              <div className="mb-3">
                <p style={{ fontSize: "12px", color: "#5F6368" }}>
                  Apply one question count to every tag. The total assessment
                  size will be this count multiplied across the listed tags.
                </p>
                <input
                  type="number"
                  min="0"
                  max={maxUniformCount}
                  value={textFieldValue}
                  onChange={handleTextFieldChange}
                  className="form-control w-100 text-start mb-2"
                />
                <p style={{ fontSize: "12px", color: "#5F6368" }}>
                  Maximum shared count: {maxUniformCount}. Each learner receives
                  a random selection from every tag.
                </p>
              </div>
            )}

            <h6 className="fw-bold">Order of Questions</h6>
            <div className="row mb-3">
              <div className="col">
                <Form.Check
                  type="radio"
                  label="Given Order"
                  name="modeOptions"
                  value="A"
                  checked={radioValue === "A"}
                  onChange={handleRadioChange}
                  className="mb-2"
                />
              </div>
              <div className="col">
                <Form.Check
                  type="radio"
                  label="Shuffled"
                  name="modeOptions"
                  value="B"
                  checked={radioValue === "B"}
                  onChange={handleRadioChange}
                  className="mb-2"
                />
              </div>
            </div>
            <Form.Check
              type="checkbox"
              label="Shuffle MCQ Options"
              checked={checkbox1}
              onChange={handleCheckbox1}
              className="mb-2"
            />
            <p style={{ fontSize: "12px", color: "#5F6368" }}>
              Ticking this will shuffle MCQ options and show them in random
              order to the learners
            </p>

            <Form.Check
              type="checkbox"
              label="Mandatory Answer"
              checked={checkbox2}
              onChange={handleCheckbox2}
              className="mb-2"
            />
            <p style={{ fontSize: "12px", color: "#5F6368" }}>
              User must attempt all the questions before submitting the quiz for
              evaluation.
            </p>
          </form>
        </div>
      )}
    </div>
  );
}

export default PowerUp;
